/* PaymentApprovalsPanel · Seller Settings → Payment Approvals tab
 *
 * Owner-only panel that configures the co-approval workflow:
 *   • Enable/disable toggle
 *   • Threshold dollar amount above which co-approval is required
 *   • Approver picker — owner selects which team members (with role
 *     `owner` or `admin`) must co-sign large payments
 *
 * Read backend: GET  /api/payment-approvals/settings
 * Write back:   PATCH /api/payment-approvals/settings
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, AlertTriangle, Loader2, UserCheck } from "lucide-react";

export function PaymentApprovalsPanel() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);

  // OWNER-ONLY view + edit (Jun 2026). Admins/operators/viewers shouldn't
  // even see the panel — the parent SellerSettings/BuyerSettings already
  // hides the tab from non-owners, but if someone deep-links via `#hash`
  // we render a defensive "owner only" message instead of the controls.
  const isOwner = (user?.team_role || "owner") === "owner";
  const canEdit = isOwner;

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/payment-approvals/settings").then(r => r.data),
        api.get("/team").then(r => r.data.members || []),
      ]);
      setSettings(s);
      setTeam(t);
    } catch (e) {
      toast.error("Couldn't load approval settings");
    }
  };
  useEffect(() => { load(); }, []);

  const toggleApprover = (uid) => {
    setSettings(s => ({
      ...s,
      approver_user_ids: (s.approver_user_ids || []).includes(uid)
        ? s.approver_user_ids.filter(x => x !== uid)
        : [...(s.approver_user_ids || []), uid],
    }));
  };

  const save = async () => {
    if (settings.enabled && (settings.approver_user_ids || []).length === 0) {
      toast.error("Pick at least one co-approver before enabling.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/payment-approvals/settings", {
        enabled: !!settings.enabled,
        threshold_usd: Number(settings.threshold_usd) || 0,
        approver_user_ids: settings.approver_user_ids || [],
      });
      setSettings(data);
      toast.success("Approval settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!isOwner) {
    return (
      <div data-testid="payment-approvals-owner-only" className="rounded-xl border border-amber-300 bg-amber-50/60 p-4 text-[13px] text-amber-900 flex gap-2 items-start max-w-xl">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
        <div>
          <div className="font-medium">Owner-only setting</div>
          <div className="mt-1 leading-relaxed">
            Payment approval rules are governed strictly by the account owner.
            Ask the owner to update the co-approval threshold or approver list
            from their Settings.
          </div>
        </div>
      </div>
    );
  }

  if (!settings) {
    return <div className="py-8 text-center text-foreground/55"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading…</div>;
  }

  const eligibleApprovers = (team || []).filter(m =>
    (m.role === "owner" || m.role === "admin") && m.status === "active");

  return (
    <div data-testid="payment-approvals-panel" className="space-y-6">
      <div>
        <h2 className="display text-2xl tracking-tight">Payment approvals</h2>
        <p className="mt-1 text-[13px] text-foreground/65 leading-relaxed max-w-xl">
          Require a second team member to sign off on large payments. The
          initiator's wallet is held in escrow until at least one approver
          signs the payment.
        </p>
      </div>

      {!canEdit && (
        <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-[12.5px] text-amber-900 flex gap-2 items-start">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
          <div>Only the account owner can change these settings. You can view the current rules below but not edit them.</div>
        </div>
      )}

      {/* Enable toggle */}
      <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-4">
        <div>
          <div className="font-medium text-foreground text-[14px] flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-lime-700"/>
            Require co-approval for large payments
          </div>
          <div className="text-[12px] text-foreground/60 mt-1 max-w-md leading-relaxed">
            When enabled, any payment above the threshold below will be held until a co-approver signs off.
          </div>
        </div>
        <Switch
          checked={!!settings.enabled} disabled={!canEdit}
          onCheckedChange={(v) => setSettings(s => ({ ...s, enabled: v }))}
          data-testid="approvals-enabled-toggle"
        />
      </div>

      {/* Threshold input — disabled if disabled */}
      <div className={`rounded-xl border border-border p-4 ${!settings.enabled ? "opacity-50" : ""}`}>
        <label className="font-medium text-foreground text-[14px] block mb-2">
          Co-approval threshold (USD)
        </label>
        <div className="flex items-center gap-2 max-w-xs">
          <span className="text-foreground/55">$</span>
          <Input
            type="number" min="0" step="1000"
            value={settings.threshold_usd ?? 25000}
            onChange={(e) => setSettings(s => ({ ...s, threshold_usd: e.target.value }))}
            disabled={!canEdit || !settings.enabled}
            className="bg-secondary/40"
            data-testid="approvals-threshold-input"
          />
        </div>
        <div className="text-[11.5px] text-foreground/55 mt-2">
          Payments at or above this amount require co-approval. Below this — no approval needed.
        </div>
      </div>

      {/* Approvers list */}
      <div className={`rounded-xl border border-border p-4 ${!settings.enabled ? "opacity-50" : ""}`}>
        <label className="font-medium text-foreground text-[14px] block mb-3 flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-lime-700"/>
          Co-approvers
        </label>
        {eligibleApprovers.length === 0 ? (
          <div className="text-[12.5px] text-foreground/60 leading-relaxed py-2">
            No eligible team members yet. Invite a team member with an <strong>Owner</strong> or <strong>Admin</strong> role first
            — Operators and Viewers can't be co-approvers.
          </div>
        ) : (
          <div className="space-y-1.5">
            {eligibleApprovers.map(m => {
              const checked = (settings.approver_user_ids || []).includes(m.user_id || m.id);
              return (
                <label key={m.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                  checked ? "bg-lime-50 border-lime-400" : "border-border hover:bg-secondary/30"
                }`}>
                  <input type="checkbox" checked={checked} disabled={!canEdit || !settings.enabled}
                    onChange={() => toggleApprover(m.user_id || m.id)}
                    className="w-4 h-4 accent-lime-600"
                    data-testid={`approver-${m.email}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-medium text-foreground truncate">{m.name || m.email}</div>
                    <div className="text-[11px] text-foreground/55 truncate">{m.email} · {m.role}</div>
                  </div>
                  {checked && <CheckCircle2 className="w-4 h-4 text-lime-600"/>}
                </label>
              );
            })}
          </div>
        )}
        <div className="text-[11.5px] text-foreground/55 mt-3 leading-relaxed">
          The initiator is automatically excluded from approving their own payment.
          Each approver enters their own SMS code (routed to the account owner's phone) to sign off.
        </div>
      </div>

      {canEdit && (
        <div className="flex justify-end">
          <Button
            onClick={save} disabled={saving}
            className="rounded-full bg-foreground text-background"
            data-testid="approvals-save"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Saving…</> : "Save changes"}
          </Button>
        </div>
      )}
    </div>
  );
}
